import { useState, useEffect, useCallback } from 'react';
import { deductionApi, DeductionDto, riderApi, RiderInfoDto } from '@/shared/api/deductionApi';
import { commonCodeApi } from '@/shared/api/commonCodeApi';
import { usePagination } from '@/shared/hooks/usePagination';
import { SelectOption } from '@/shared/components/forms/SearchableSelect';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { getItem } from '@/shared/utils/storage';
import { formatCurrency } from '@/shared/utils/formatter';
import { NewDeductionForm, EMPTY_FORM } from '../types/riderToPartner.types';
import { normalizeDate, calcEndDate, calcDays } from '../utils/riderToPartnerHelpers';

const ITEMS_PER_PAGE = 10;

export function useRiderToPartnerData(supplierId: string) {
    const { showToast } = useToast();

    const [loading, setLoading] = useState(false);
    const [ridersLoading, setRidersLoading] = useState(false);
    const [deductionTypesLoading, setDeductionTypesLoading] = useState(false);

    const [deductions, setDeductions] = useState<DeductionDto[]>([]);
    const [riders, setRiders] = useState<RiderInfoDto[]>([]);
    const [deductionTypes, setDeductionTypes] = useState<Array<{ code: string; name: string }>>([]);

    const [filterRiderId, setFilterRiderId] = useState('');
    const [newDeduction, setNewDeduction] = useState<NewDeductionForm>(EMPTY_FORM);

    const pagination = usePagination({ items: deductions, itemsPerPage: ITEMS_PER_PAGE });

    const fetchDeductionTypes = useCallback(async () => {
        try {
            setDeductionTypesLoading(true);
            const response = await commonCodeApi.getCommonCodeList('FEE_DEDUCT');
            setDeductionTypes(
                response.codes
                    .filter((c) => c.useYn === 'Y')
                    .map((c) => ({ code: c.comCode, name: c.comCdName }))
            );
        } catch {
            showToast('공제 타입 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setDeductionTypesLoading(false);
        }
    }, [showToast]);

    const fetchRiders = useCallback(async () => {
        if (!supplierId) return;
        try {
            setRidersLoading(true);
            const response = await riderApi.getRiderList(supplierId);
            setRiders(response.riders || []);
        } catch {
            showToast('라이더 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setRiders([]);
        } finally {
            setRidersLoading(false);
        }
    }, [supplierId, showToast]);

    const fetchDeductions = useCallback(
        async (riderId?: string) => {
            if (!supplierId) return;
            try {
                setLoading(true);
                const response = await deductionApi.getDeductionList({
                    suppId: supplierId,
                    deductTarget: 'R',
                });
                let filtered = (response.deducts || []).filter(
                    (d) => d.deductTarget === 'R' && d.deductRegType === 'S' && d.useYn !== 'N'
                );
                if (riderId) filtered = filtered.filter((d) => d.riderId === riderId);
                filtered.sort((a, b) => b.seqNo - a.seqNo);
                setDeductions(filtered);
            } catch {
                showToast('일차감 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
                setDeductions([]);
            } finally {
                setLoading(false);
            }
        },
        [supplierId, showToast]
    );

    useEffect(() => {
        fetchDeductionTypes();
        fetchRiders();
        fetchDeductions();
    }, [fetchDeductionTypes, fetchRiders, fetchDeductions]);

    const handleSearch = () => {
        fetchDeductions(filterRiderId);
        pagination.resetPagination();
    };

    const handleDelete = async (seqNo: number) => {
        if (!window.confirm('해당 일차감을 삭제(비활성화)하시겠습니까?')) return;
        try {
            const userId = getItem<string>('USER_ID') || '';
            await deductionApi.toggleUseYn({ seqNos: [seqNo], useYn: 'N', updUser: userId });
            setDeductions((prev) => prev.filter((d) => d.seqNo !== seqNo));
            showToast('일차감이 삭제되었습니다.', { type: ToastType.SUCCESS });
        } catch {
            showToast('일차감 삭제에 실패했습니다.', { type: ToastType.ERROR });
        }
    };

    // ── 날짜·일수 연동 ──────────────────────────────────────────────────────
    const handleStartDateChange = (value: string) => {
        const normalized = normalizeDate(value);
        const days = parseInt(newDeduction.deductDays, 10);
        if (normalized && days > 0) {
            setNewDeduction((p) => ({ ...p, startDate: normalized, endDate: calcEndDate(normalized, days) }));
        } else if (normalized && newDeduction.endDate) {
            const d = calcDays(normalized, newDeduction.endDate);
            setNewDeduction((p) => ({ ...p, startDate: normalized, deductDays: d > 0 ? String(d) : '' }));
        } else {
            setNewDeduction((p) => ({ ...p, startDate: normalized }));
        }
    };

    const handleEndDateChange = (value: string) => {
        const normalized = normalizeDate(value);
        if (normalized && newDeduction.startDate) {
            const d = calcDays(newDeduction.startDate, normalized);
            setNewDeduction((p) => ({ ...p, endDate: normalized, deductDays: d > 0 ? String(d) : '' }));
        } else {
            setNewDeduction((p) => ({ ...p, endDate: normalized }));
        }
    };

    const handleDeductDaysChange = (value: string) => {
        const days = parseInt(value, 10);
        if (newDeduction.startDate && days > 0) {
            setNewDeduction((p) => ({ ...p, deductDays: value, endDate: calcEndDate(newDeduction.startDate, days) }));
        } else {
            setNewDeduction((p) => ({ ...p, deductDays: value }));
        }
    };
    // ────────────────────────────────────────────────────────────────────────

    const handleAdd = async () => {
        const { riderId, deductionTypeCode, deductionName, deductionAmount, startDate, endDate } = newDeduction;
        if (!riderId) { showToast('라이더를 선택해주세요.', { type: ToastType.WARNING }); return; }
        if (!deductionTypeCode) { showToast('공제타입을 선택해주세요.', { type: ToastType.WARNING }); return; }
        if (!deductionName) { showToast('공제명을 입력해주세요.', { type: ToastType.WARNING }); return; }
        if (!deductionAmount) { showToast('공제금액을 입력해주세요.', { type: ToastType.WARNING }); return; }
        if (!startDate) { showToast('시작일자를 입력해주세요.', { type: ToastType.WARNING }); return; }
        if (!endDate) { showToast('종료일자를 입력해주세요.', { type: ToastType.WARNING }); return; }
        if (endDate < startDate) { showToast('종료일자는 시작일자 이후여야 합니다.', { type: ToastType.WARNING }); return; }

        try {
            const userId = getItem<string>('USER_ID') || '';
            const deductCnt = calcDays(startDate, endDate);
            const response = await deductionApi.registerDeduction({
                deductTarget: 'R',
                deductRegType: 'S',
                suppId: supplierId,
                riderId,
                deductType: deductionTypeCode,
                calcType: '1',
                deductName: deductionName,
                deductCnt: deductCnt > 0 ? deductCnt : null,
                deductAmt: parseInt(deductionAmount, 10),
                deductDispYn: 'Y',
                startDay: startDate,
                endDay: endDate,
                insUser: userId,
            });
            showToast('일차감이 등록되었습니다.', { type: ToastType.SUCCESS });
            setDeductions((prev) => [
                {
                    seqNo: response.seqNo || Date.now(),
                    deductTarget: 'R',
                    deductRegType: 'S',
                    suppId: supplierId,
                    riderId,
                    deductType: deductionTypeCode,
                    calcType: '1',
                    deductName: deductionName,
                    deductCnt: deductCnt > 0 ? deductCnt : null,
                    deductAmt: parseInt(deductionAmount, 10),
                    deductRate: null,
                    deductDispYn: 'Y',
                    startDay: startDate,
                    endDay: endDate,
                    insDate: new Date().toISOString(),
                    insUser: userId,
                    updDate: null,
                    updUser: null,
                },
                ...prev,
            ]);
            setNewDeduction(EMPTY_FORM);
            pagination.resetPagination();
        } catch {
            showToast('일차감 등록에 실패했습니다.', { type: ToastType.ERROR });
        }
    };

    // ── 파생 값 ──────────────────────────────────────────────────────────────
    const riderOptions: SelectOption[] = riders.map((r) => ({
        value: r.riderId,
        label: r.riderName ? `${r.riderName}(${r.riderId})` : r.riderId,
    }));

    const getRiderLabel = (riderId: string): string => {
        const r = riders.find((x) => x.riderId === riderId);
        return r ? (r.riderName ? `${r.riderName}(${r.riderId})` : r.riderId) : riderId;
    };

    const getDeductionTypeName = (code: string): string =>
        deductionTypes.find((t) => t.code === code)?.name ?? code;

    const previewDays =
        newDeduction.startDate && newDeduction.endDate
            ? calcDays(newDeduction.startDate, newDeduction.endDate)
            : 0;
    const previewTotal =
        previewDays > 0 && newDeduction.deductionAmount
            ? previewDays * parseInt(newDeduction.deductionAmount, 10)
            : 0;

    const formatAmountDisplay = (amount: string) => formatCurrency(amount);

    return {
        loading,
        ridersLoading,
        deductionTypesLoading,
        deductions,
        riders,
        deductionTypes,
        filterRiderId,
        setFilterRiderId,
        newDeduction,
        setNewDeduction,
        pagination,
        handleSearch,
        handleDelete,
        handleStartDateChange,
        handleEndDateChange,
        handleDeductDaysChange,
        handleAdd,
        riderOptions,
        getRiderLabel,
        getDeductionTypeName,
        previewDays,
        previewTotal,
        formatAmountDisplay,
    };
}
